import { EmptyState, FlexColumn, FlexRow, Loading, MaterialIcon, useLoading } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { Modal, ModalBody, ModalHeader } from 'baseui/modal';
import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useStyles } from '../../../hooks/useStyles';
import { getAsset } from '../../../services/assetService';
import { Asset } from '../../../types/asset';
import StyledTooltip from '../../ui/StyledTooltip';
import { AssetForm } from '../AssetForm';
import { AssetInfo } from '../AssetInfo';

type Props = {
  isOpen: boolean;
  selectedAssetId?: string;
  onClose: (update?: boolean) => void;
  clientId?: string;
  subClientId?: string;
};

function AssetDetailModal({ onClose, isOpen, selectedAssetId, clientId, subClientId }: Props) {
  const { css, theme } = useStyles();
  const [asset, setAsset] = useState<Asset>();
  const { startLoading, stopLoading, loading } = useLoading(false);
  const [editingMode, setEditingMode] = useState(false);
  const [error, setError] = useState(false);

  const loadAssetData = async (assetId: string) => {
    startLoading();
    const response = await getAsset(assetId);
    if (response?.error) {
      setAsset(undefined);
      setError(true);
    } else {
      setAsset(response?.asset);
      setError(false);
    }
    stopLoading();
  };

  useEffect(() => {
    if (isOpen && selectedAssetId) setEditingMode(false);
  }, [isOpen]);

  useEffect(() => {
    if (!selectedAssetId) {
      setAsset(undefined);
      setEditingMode(true);
      return;
    }
    loadAssetData(selectedAssetId);
  }, [selectedAssetId]);

  return createPortal(
    <Modal
      animate
      closeable
      onClose={() => onClose()}
      isOpen={isOpen}
      size="auto"
      overrides={{
        Root: {
          style: {
            overflow: 'hidden',
          },
        },
        Dialog: {
          style: {
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {loading && (
        <FlexColumn
          centered
          classNames={css({
            height: '400px',
            width: '400px',
          })}
        >
          <Loading />
        </FlexColumn>
      )}
      {!loading && error && (
        <FlexColumn
          centered
          classNames={css({
            height: '400px',
            width: '400px',
          })}
        >
          <EmptyState
            title="*Error*"
            description="No se pudieron obtener los datos del activo"
          />
        </FlexColumn>
      )}
      {!loading && !error && (
        <>
          <FlexRow
            gap={0}
            classNames={css({
              justifyContent: 'space-between',
              margin: '6px 2rem 0px 0px',
            })}
          >
            <ModalHeader
              className={css({
                marginBottom: '0 !important',
              })}
            >
              {asset?.label ?? 'Nuevo'}
            </ModalHeader>
            {!editingMode && (
              <StyledTooltip
                content={'Editar'}
                showArrow={false}
              >
                <Button
                  size="compact"
                  shape="square"
                  onClick={() => setEditingMode(true)}
                  kind="tertiary"
                >
                  <MaterialIcon
                    name={'edit'}
                    size="xs-mini"
                  />
                </Button>
              </StyledTooltip>
            )}
          </FlexRow>
          <ModalBody
            className={css({
              borderTop: '1px solid rgb(226, 226, 226)',
              boxSizing: 'border-box',
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              maxHeight: '85vh',
              maxWidth: '1200px',
              minWidth: '350px',
              overflow: 'auto',
              padding: theme.sizing.scale600,
              width: '75vw',
            })}
          >
            {!editingMode && asset && <AssetInfo asset={asset} />}
            {editingMode && !loading && (
              <AssetForm
                asset={asset}
                onCancel={() => setEditingMode(false)}
                afterSave={() => onClose(true)}
                clientId={clientId}
                subClientId={subClientId}
              />
            )}
          </ModalBody>
        </>
      )}
    </Modal>,
    document.body,
  );
}

export default memo(AssetDetailModal);
